import { useMutation, useQueryClient } from "react-query";

import { updateFrequencyCareCoordination } from "api/customers";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_CUSTOMER_PHYSICIANS } from "@constants/reactQueryKeys";

interface UpdateFrequencyCareCoordinationPayload {
  clientId: string;
  frequency: string | null;
}

/**
 * Custom hook to update the frequency of care coordination for a client.
 *
 * @param options - Optional configuration for success and error handling.
 * @returns An object containing the updateFrequencyAction function, loading state, and other mutation info.
 */
const useUpdateFrequencyCareCoordination = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: updateFrequencyAction,
    mutateAsync: updateFrequencyAsync,
    isLoading: isUpdatingFrequency,
    ...rest
  } = useMutation(
    ({ clientId, frequency }: UpdateFrequencyCareCoordinationPayload) =>
      updateFrequencyCareCoordination(clientId, frequency),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Frequency of care coordination saved" });
        queryClient.invalidateQueries([GET_CUSTOMER_PHYSICIANS, variables.clientId]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error || "Unable to save frequency of care coordination";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { updateFrequencyAction, updateFrequencyAsync, isUpdatingFrequency, ...rest };
};

export default useUpdateFrequencyCareCoordination;
